import * as React from 'react';
import { useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import { apiRawFetch } from '../../../../APIClient';
import { APIError } from '../../../../APIError';
import { ROUTE_PATH } from '../../../../constants';
import { CredentialRequest, CredentialSecret } from '../../../../types';
import ErrorJumbotron from '../../../../widgets/ErrorJumbotron';
import LoadingIndicator from '../../../../widgets/LoadingIndicator';
import Panel from '../../../../widgets/Panel';
import ViewHeader from '../../../../widgets/ViewHeader';
import CloudCredentialForm from './ConnectedAccountForm';
import { PageTitle } from '../../../../widgets/PageTitle';
import Viewport from '../../../../widgets/Viewport';
import { memoizedCredentialSchemaSelector } from '../../../../selectors/credentialSchemaSelectors';
import { useRedirectCallback, useTrashablePromise } from '../../../../hooks';
import { SubmissionReview } from './SubmissionReview';
import { useFetchCredential } from './useFetchCredential';

const SCHEMA_NOT_FOUND = new APIError(
  'Unable to determine type of credential. Please contact your CST.',
  500,
);

type AccountEditProps = {
  credential: CredentialSecret;
};

export const AccountEdit: React.FunctionComponent<AccountEditProps> = ({
  credential,
}) => {
  const schemaSelector = React.useMemo(
    () => memoizedCredentialSchemaSelector.getSchemaByType(credential.type),
    [credential.type],
  );
  const schema = useSelector(schemaSelector);

  const onGoBack = useRedirectCallback(
    ROUTE_PATH.SELF_SERVICE.CONNECTED_ACCOUNTS.MAIN,
  );

  const { registerPromise } = useTrashablePromise();

  const [submission, setSubmission] = React.useState<CredentialSecret | null>(
    null,
  );

  const onSubmit = React.useCallback(
    async (payload: CredentialRequest, setError: (error: APIError) => void) => {
      try {
        const { credential: submission } = await registerPromise(
          apiRawFetch.editCredential(credential.uuid, payload),
        );
        setSubmission(submission);
      } catch (err) {
        setError(err);
      }
    },
    [credential.uuid, registerPromise],
  );

  if (!schema) {
    return <ErrorJumbotron error={SCHEMA_NOT_FOUND} />;
  }

  if (submission) {
    return <SubmissionReview credential={submission} />;
  }

  return (
    <CloudCredentialForm
      onCancel={onGoBack}
      onSubmit={onSubmit}
      schema={schema}
      credential={credential}
    />
  );
};

const ConnectedAccountEditView = () => {
  const { uuid } = useParams<{ uuid: string }>();
  const { credential, error, isLoading } = useFetchCredential(uuid);

  let content: React.ReactNode;

  if (error) {
    content = <ErrorJumbotron error={error} />;
  } else if (isLoading || !credential) {
    content = (
      <LoadingIndicator size="lg" centered>
        Loading credential...
      </LoadingIndicator>
    );
  } else {
    content = <AccountEdit credential={credential} />;
  }

  return (
    <Viewport>
      <PageTitle title="Edit Account" />
      <ViewHeader title="Edit Account" />

      <Panel className="min-height-for-loading-indicator">{content}</Panel>
    </Viewport>
  );
};

export default ConnectedAccountEditView;
